import {defineStore} from 'pinia'
import {EstimationNs} from "@shared/types";
import {EstimationApi} from "@/api";

let summaryInFlight = false;
let summaryQueued = false;

export const useEstimationStore = defineStore("estimation", {
  state: () => ({
    featureMap: new Map<string, EstimationNs.FeatureEstimation>,
    featureTree: [] as EstimationNs.EstimationTreeNode[],
    estimations: [] as EstimationNs.ProjectEstimation[],
    selectedEstimation: undefined as EstimationNs.ProjectEstimation | undefined,
    selectedGroup: undefined as EstimationNs.EstimationGroup | undefined,
    selectedFeatureEstimation: undefined as EstimationNs.FeatureEstimation | undefined,
    groupSummary: [] as EstimationNs.GroupSummary[],
    recipients: [] as EstimationNs.Recipient[],
    activeDetailTab: 'feature' as 'feature' | 'screens',
    isTreeExpanded: true,
    showHiddenFeatures: false,
    isAiEstimating: false
  }),

  getters: {
    getEstimations: (state) => state.estimations,
    getSelectedEstimation: (state) => state.selectedEstimation,
    getGroups: (state) => {
      // Sort groups by groupId in ascending order (smallest first)
      if (state.selectedEstimation?.groups) {
        return [...state.selectedEstimation.groups].sort((a, b) => a.groupId - b.groupId);
      }
      return undefined;
    },
    getSelectedGroup: (state) => state.selectedGroup,
    getSelectedFeatureEstimation: (state) => state.selectedFeatureEstimation,
    getGroupSummaryData: (state) => state.groupSummary,
    getRecipients: (state) => state.recipients,
    getActiveDetailTab: (state) => state.activeDetailTab,
    getIsTreeExpanded: (state) => state.isTreeExpanded,
    getShowHiddenFeatures: (state) => state.showHiddenFeatures,
    getIsAiEstimating: (state) => state.isAiEstimating
  },

  actions: {
    convertFlatDataToTree() {
      const map = new Map<string, EstimationNs.EstimationTreeNode>;

      this.featureMap.forEach((item: EstimationNs.FeatureEstimation, key: string) => {
        // Spread properties like feature store does to make color directly accessible
        map.set(key, {key, data: {...item, ...(item as any).properties}, children: []} as EstimationNs.EstimationTreeNode);
      });

      const hierarchy: EstimationNs.EstimationTreeNode[] = [];
      map.forEach((item: EstimationNs.EstimationTreeNode, key: string) => {
        if (item.data.parentId) {
          const parentItem = map.get(item.data.parentId);
          if (parentItem) {
            // Find parent color for inheritance (like feature store)
            const parentColor = (parentItem.data as any).parentColor ?? (parentItem.data as any).color;
            if (parentColor && !(item.data as any).parentColor) {
              (item.data as any).parentColor = parentColor;
            }
            parentItem.children!.push(item!);
          }
        } else {
          hierarchy.push(item!);
        }
      });

      this.featureTree = hierarchy;
      return hierarchy;
    },

    // Load estimation data for a project
    async loadFeatureEstimationData(workspaceId: string, projectId: string) {
      const payload: EstimationNs.GetFeatureEstimationsRequest = {
        workspaceId,
        projectId,
        estimationId: this.selectedEstimation?.estimationId,
        groupId: this.selectedGroup?.groupId
      };

      return EstimationApi.getEstimationData(payload).then((featureList) => {
        this.featureMap.clear();
        featureList.forEach(item => {
          this.featureMap.set(item.featureId, item as any);
        });
        this.convertFlatDataToTree();
        return featureList;
      });
    },

    // Save estimation settings
    async createEstimationSettings(workspaceId: string, projectId: string, formData: any): Promise<EstimationNs.ProjectEstimation> {
      const payload: EstimationNs.UpdateEstimationRequest = {
        workspaceId,
        projectId,
        ...formData
      };

      return EstimationApi.createEstimationSettings(payload)
      .then(async (response) => {
        await this.loadProjectEstimations(workspaceId, projectId);
        return response;
      })
    },

    // Save estimation settings
    async updateEstimationSettings(workspaceId: string, projectId: string, formData: any): Promise<EstimationNs.ProjectEstimation> {
      if (!this.selectedEstimation) {
        throw new Error('No selected estimation found');
      }

      const payload: EstimationNs.UpdateEstimationRequest = {
        workspaceId,
        projectId,
        estimationId: this.selectedEstimation.estimationId,
        ...formData
      };

      return EstimationApi.updateEstimationSettings(payload)
      .then(async (response) => {
        this.selectedEstimation = response;
        return response;
      })
    },

    // Get estimation settings for a project
    loadProjectEstimations: async function (workspaceId: string, projectId: string, selectLastGroup: boolean = false): Promise<EstimationNs.ProjectEstimation[]> {
      const payload: EstimationNs.GetEstimationsRequest = {
        workspaceId,
        projectId
      };

      return EstimationApi.getProjectEstimations(payload)
      .then((response) => {
        this.estimations = response ? response : [];
        this.selectedEstimation = (response && response.length > 0) ? response[0] : undefined;

        // Sort groups by groupId in ascending order (smallest first)
        if (this.selectedEstimation && this.selectedEstimation.groups) {
          this.selectedEstimation.groups.sort((a, b) => a.groupId - b.groupId);
        }

        if (selectLastGroup) {
          this.selectedGroup = this.selectedEstimation ? this.selectedEstimation.groups[this.selectedEstimation.groups.length - 1] : undefined;
        } else {
          this.selectedGroup = this.selectedEstimation ? this.selectedEstimation.groups[0] : undefined;
        }
        return response
      })
    },

    // Update feature estimation (real-time updates)
    async updateFeatureEstimation(
        workspaceId: string,
        projectId: string,
        featureId: string,
        updates: {
          moscow?: EstimationNs.MoSCoWPriority;
          priority?: EstimationNs.Priority;
          estimatedHours?: number;
          hourlyRate?: number;
        }
    ): Promise<EstimationNs.FeatureEstimation> {
      const request: EstimationNs.UpdateFeatureEstimationRequest = {
        workspaceId,
        projectId,
        groupId: this.selectedGroup?.groupId ?? 0,
        featureId,
        ...updates
      };

      return EstimationApi.updateFeatureEstimation(request).then((featureEstimation) => {
        // Preserve existing color data if API response doesn't include it
        const existingFeature = this.featureMap.get(featureId);
        if (existingFeature) {
          const existingColor = (existingFeature as any).properties?.color || (existingFeature as any).color;
          const existingProperties = (existingFeature as any).properties || {};
          
          // Merge existing properties (especially color) with new data
          if (existingColor && !(featureEstimation as any).properties?.color && !(featureEstimation as any).color) {
            (featureEstimation as any).properties = {
              ...existingProperties,
              ...((featureEstimation as any).properties || {}),
              color: existingColor
            };
          } else if (existingProperties && Object.keys(existingProperties).length > 0) {
            // Merge other existing properties
            (featureEstimation as any).properties = {
              ...existingProperties,
              ...((featureEstimation as any).properties || {})
            };
          }
        }
        
        // Update local store data immediately
        this.featureMap.set(featureId, featureEstimation);

        // Rebuild tree to reflect changes
        this.convertFlatDataToTree();

        // The hours changed, so the foot of the table did too — see refreshGroupSummary.
        void this.refreshGroupSummary(workspaceId, projectId);

        return featureEstimation
      });
    },

    async runAiEstimation(workspaceId: string, projectId: string): Promise<EstimationNs.AiEstimateResult[]> {
      if (!this.selectedEstimation || !this.selectedGroup) {
        throw new Error('No selected estimation/group found');
      }
      this.isAiEstimating = true;
      try {
        const payload: EstimationNs.AiEstimateRequest = {
          workspaceId,
          projectId,
          estimationId: this.selectedEstimation.estimationId,
          groupId: this.selectedGroup.groupId
        };
        const results = await EstimationApi.aiEstimate(payload);

        // Sonuçları öneri olarak göstermek için feature verisini tazele
        await this.loadFeatureEstimationData(workspaceId, projectId);

        return results;
      } finally {
        this.isAiEstimating = false;
      }
    },

    async discardAiEstimate(workspaceId: string, projectId: string, featureId: string): Promise<void> {
      await this.discardAiEstimates(workspaceId, projectId, [featureId]);
    },

    async discardAiEstimates(workspaceId: string, projectId: string, featureIds: string[]): Promise<void> {
      const cleared: string[] = [];
      featureIds.forEach((featureId) => {
        const feature = this.featureMap.get(featureId);
        if (!feature) {
          return;
        }
        feature.aiEstimatedHours = undefined;
        feature.aiComplexityScore = undefined;
        feature.aiConfidence = undefined;
        feature.aiReasoning = undefined;
        feature.aiSource = undefined;
        cleared.push(featureId);
      });

      if (!cleared.length) {
        return;
      }

      this.convertFlatDataToTree();

      if (!this.selectedEstimation || !this.selectedGroup) {
        return;
      }

      const payload: EstimationNs.DiscardAiEstimateRequest = {
        workspaceId,
        projectId,
        estimationId: this.selectedEstimation.estimationId,
        groupId: this.selectedGroup.groupId,
        featureIds: cleared
      };

      try {
        await EstimationApi.discardAiEstimate(payload);
      } catch (error) {
        // The stored suggestion is still there; show what is stored.
        await this.loadFeatureEstimationData(workspaceId, projectId);
        throw error;
      }
    },

    async acceptAiEstimate(workspaceId: string, projectId: string, featureId: string): Promise<void> {
      await this.acceptAiEstimates(workspaceId, projectId, [featureId]);
    },

    async acceptAiEstimates(workspaceId: string, projectId: string, featureIds: string[]): Promise<void> {
      const hours = new Map<string, number>();
      featureIds.forEach((featureId) => {
        const feature = this.featureMap.get(featureId);
        if (feature && feature.aiEstimatedHours != null) {
          hours.set(featureId, feature.aiEstimatedHours);
        }
      });

      if (!hours.size) {
        return;
      }

      const results = await Promise.allSettled(
        [...hours].map(([featureId, estimatedHours]) =>
          this.updateFeatureEstimation(workspaceId, projectId, featureId, {estimatedHours})
        )
      );

      const failed = results.filter((result) => result.status === 'rejected');
      if (failed.length) {
        console.error('Some estimates were not accepted', failed);
        throw new Error(`${failed.length} of ${hours.size} estimates were not accepted`);
      }
    },

    // Toggle tree expand/collapse
    toggleTreeExpanded() {
      this.isTreeExpanded = !this.isTreeExpanded;
    },

    // Toggle show/hide hidden features
    toggleShowHiddenFeatures() {
      this.showHiddenFeatures = !this.showHiddenFeatures;
    },

    // Helper function to collect all child feature IDs recursively
    collectAllFeatureIds(featureId: string): string[] {
      const featureIds: string[] = [featureId];
      const feature = this.featureMap.get(featureId);

      if (!feature) {
        return featureIds;
      }

      // Find all children of this feature
      this.featureMap.forEach((f) => {
        if (f.parentId === featureId) {
          // Recursively get children's children
          featureIds.push(...this.collectAllFeatureIds(f.featureId));
        }
      });

      return featureIds;
    },

    // Toggle feature hidden state
    async toggleFeatureHidden(
        workspaceId: string,
        projectId: string,
        featureId: string,
        hidden: boolean
    ): Promise<EstimationNs.FeatureEstimation[]> {
      // Collect the feature and all its children
      const featureIds = this.collectAllFeatureIds(featureId);

      const request: EstimationNs.ToggleFeatureHiddenRequest = {
        workspaceId,
        projectId,
        groupId: this.selectedGroup?.groupId ?? 0,
        featureIds,
        hidden
      };

      return EstimationApi.toggleFeatureHidden(request).then((featureEstimations) => {
        // Update local store data for all returned features
        featureEstimations.forEach((featureEstimation) => {
          // Preserve existing color data if API response doesn't include it
          const existingFeature = this.featureMap.get(featureEstimation.featureId);
          if (existingFeature) {
            const existingColor = (existingFeature as any).properties?.color || (existingFeature as any).color;
            const existingProperties = (existingFeature as any).properties || {};
            
            // Merge existing properties (especially color) with new data
            if (existingColor && !(featureEstimation as any).properties?.color && !(featureEstimation as any).color) {
              (featureEstimation as any).properties = {
                ...existingProperties,
                ...((featureEstimation as any).properties || {}),
                color: existingColor
              };
            } else if (existingProperties && Object.keys(existingProperties).length > 0) {
              // Merge other existing properties
              (featureEstimation as any).properties = {
                ...existingProperties,
                ...((featureEstimation as any).properties || {})
              };
            }
          }
          this.featureMap.set(featureEstimation.featureId, featureEstimation);
        });

        // Rebuild tree to reflect changes
        this.convertFlatDataToTree();

        // Left out means it stops counting, so the total is a different number now.
        void this.refreshGroupSummary(workspaceId, projectId);

        return featureEstimations;
      });
    },

    // Set selected feature estimation
    setSelectedFeatureEstimation(feature: EstimationNs.FeatureEstimation | undefined) {
      this.selectedFeatureEstimation = feature;
    },

    // Set selected feature active detail tab
    setActiveDetailTab(activeTab: 'feature' | 'screens' | 'feature') {
      this.activeDetailTab = activeTab;
    },

    // Delete estimation group
    async deleteGroup(workspaceId: string, projectId: string, estimationId: number, groupId: number): Promise<EstimationNs.ProjectEstimation> {
      const request: EstimationNs.DeleteGroupRequest = {
        workspaceId,
        projectId,
        estimationId,
        groupId
      };

      return EstimationApi.deleteGroup(request).then(async (response) => {
        // Reload project estimations to get updated data
        await this.loadProjectEstimations(workspaceId, projectId);
        return response;
      });
    },

    // Delete estimation
    async deleteEstimation(workspaceId: string, projectId: string, estimationId: number): Promise<void> {
      const request: EstimationNs.DeleteEstimationRequest = {
        workspaceId,
        projectId,
        estimationId
      };

      return EstimationApi.deleteEstimation(request).then(async () => {
        // Clear all estimation-related data immediately
        this.selectedEstimation = undefined;
        this.selectedGroup = undefined;
        this.selectedFeatureEstimation = undefined;
        this.featureMap.clear();
        this.featureTree = [];
        this.groupSummary = [];
        this.recipients = [];

        // Reload project estimations to get updated data
        await this.loadProjectEstimations(workspaceId, projectId);

        // Ensure estimations array is empty if no estimations exist
        if (!this.estimations || this.estimations.length === 0) {
          this.estimations = [];
          this.selectedEstimation = undefined;
          this.selectedGroup = undefined;
        }
      });
    },

    // Save estimation group
    async createEstimationGroup(workspaceId: string, projectId: string, formData: any): Promise<EstimationNs.EstimationGroup> {
      const payload: EstimationNs.UpdateEstimationRequest = {
        workspaceId,
        projectId,
        ...formData
      };

      return EstimationApi.createGroup(payload)
      .then(async (response) => {
        await this.loadProjectEstimations(workspaceId, projectId, true);

        return response;
      })
    },

    async refreshGroupSummary(workspaceId: string, projectId: string): Promise<void> {
      const estimationId = this.selectedEstimation?.estimationId;
      if (estimationId === undefined) {
        return;
      }

      if (summaryInFlight) {
        summaryQueued = true;
        return;
      }

      summaryInFlight = true;
      try {
        do {
          summaryQueued = false;
          await this.getGroupSummary(workspaceId, projectId, estimationId);
        } while (summaryQueued);
      } catch (error) {
        console.error('Could not refresh the totals', error);
      } finally {
        summaryInFlight = false;
      }
    },

    async getGroupSummary(workspaceId: string, projectId: string, estimationId: number): Promise<EstimationNs.GroupSummary[]> {
      const payload: EstimationNs.GetGroupSummaryRequest = {
        workspaceId,
        projectId,
        estimationId
      };

      return EstimationApi.getGroupSummary(payload).then(async (response) => {
        this.groupSummary = response;
        return response;
      });
    },

    async updateGroupVisibility(payload: EstimationNs.UpdateGroupVisibilityRequest): Promise<EstimationNs.EstimationGroup> {
      const summary = this.groupSummary.find(g => g.groupId === payload.groupId);
      const was = summary?.visibleOnPublic;

      if (summary) {
        summary.visibleOnPublic = payload.visibleOnPublic;
      }

      try {
        const response = await EstimationApi.updateGroupVisibility(payload);
        if (summary) {
          summary.visibleOnPublic = response.visibleOnPublic;
        }
        return response;
      } catch (error) {
        if (summary && was !== undefined) {
          summary.visibleOnPublic = was;
        }
        throw error;
      }
    },

    // Update estimation group (name, hourly rate, currency, etc.)
    async updateEstimationGroup(workspaceId: string, projectId: string, groupId: number, updates: {
      name?: string;
      hourlyRate?: number;
      currency?: string;
      visibleOnPublic?: boolean;
    }): Promise<EstimationNs.EstimationGroup> {
      if (!this.selectedEstimation) {
        throw new Error('No selected estimation found');
      }

      const payload: EstimationNs.UpdateGroupRequest = {
        workspaceId,
        projectId,
        estimationId: this.selectedEstimation.estimationId,
        groupId,
        name: updates.name ?? '',
        hourlyRate: updates.hourlyRate ?? 0,
        currency: updates.currency ?? this.selectedEstimation.currency ?? 'USD',
        visibleOnPublic: updates.visibleOnPublic ?? false
      };

      return EstimationApi.updateGroup(payload).then(async (response) => {
        // Update the group in the selected estimation and sort by groupId
        if (this.selectedEstimation && this.selectedEstimation.groups) {
          const groupIndex = this.selectedEstimation.groups.findIndex(g => g.groupId === groupId);
          if (groupIndex >= 0) {
            // Update the group fields
            const existingGroup = this.selectedEstimation.groups[groupIndex];
            if (existingGroup) {
              existingGroup.name = response.name;
              existingGroup.hourlyRate = response.hourlyRate;
              existingGroup.currency = response.currency;
              existingGroup.visibleOnPublic = response.visibleOnPublic;
            }
          }
          // Sort groups by groupId in ascending order (smallest first)
          this.selectedEstimation.groups.sort((a, b) => a.groupId - b.groupId);
        }
        return response;
      });
    },

    // Update project currency
    async updateProjectCurrency(workspaceId: string, projectId: string, currency: string): Promise<EstimationNs.ProjectEstimation> {
      if (!this.selectedEstimation) {
        throw new Error('No selected estimation found');
      }

      const payload: EstimationNs.UpdateProjectCurrencyRequest = {
        workspaceId,
        projectId,
        estimationId: this.selectedEstimation.estimationId,
        currency
      };

      return EstimationApi.updateProjectCurrency(payload).then((response) => {
        // Update local state with the response this.selectedEstimation = response;
        return response;
      });
    },

    // Load recipients for an estimation
    async loadRecipients(workspaceId: string, projectId: string, estimationId: number): Promise<EstimationNs.Recipient[]> {
      const payload: EstimationNs.GetRecipientsRequest = {
        workspaceId,
        projectId,
        estimationId
      };

      return EstimationApi.getRecipients(payload).then((response) => {
        this.recipients = response;
        return response;
      });
    },

    // Send estimation to recipients
    async sendEstimation(workspaceId: string, projectId: string, estimationId: number, recipients: EstimationNs.Recipient[]): Promise<void> {
      const payload: EstimationNs.SendEstimationRequest = {
        workspaceId,
        projectId,
        estimationId,
        recipients
      };

      return EstimationApi.sendEstimation(payload);
    },

    // Delete a recipient from an estimation
    async deleteRecipient(workspaceId: string, projectId: string, estimationId: number, recipientId: number): Promise<void> {
      const payload: EstimationNs.DeleteRecipientRequest = {
        workspaceId,
        projectId,
        estimationId,
        recipientId
      };

      return EstimationApi.deleteRecipient(payload).then(() => {
        // Remove from local state
        this.recipients = this.recipients.filter(r => r.recipientId !== recipientId);
      });
    },

    async fetchEstimationConfig(
      workspaceId: string,
      projectId: string,
      workspaceDefault: boolean,
    ): Promise<EstimationNs.EstimationConfig | undefined> {
      return EstimationApi.getEstimationConfig({workspaceId, projectId, workspaceDefault});
    },

    async saveEstimationConfig(
      payload: EstimationNs.SaveEstimationConfigRequest,
    ): Promise<unknown> {
      return EstimationApi.saveEstimationConfig(payload);
    }
  }
});
